import axios from 'axios'
import { Logo } from 'components/elements/logo/logo'
import { Search } from 'components/elements/search/search'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  filterOptionsType,
  filterPropsType,
  filterType,
  pagedFilterType,
  searchBarPropsType,
} from 'types'
import { Filter } from '../filter/filter'
import { PageNav } from '../page-nav/page-nav'
import './_list-search-panel-block.scss'
import { ReactComponent as ClossRectangleIcon } from 'assets/icons/cross-rectangle.svg'
import { AnimatePresence, motion } from 'framer-motion'

interface ListSearchPanelBlock<T> {
  queryUrl: string
  searchBarProps?: searchBarPropsType
  filterProps?: filterPropsType
  defaultFilter: pagedFilterType
  noResultsText: string
  errorMessage: string
  displayData: (results: T[]) => React.ReactNode
  closeButtonText: string
  resultsText: string
}

export const ListSearchPanelBlock = <T extends object>({
  queryUrl,
  searchBarProps,
  filterProps,
  defaultFilter,
  noResultsText,
  errorMessage,
  displayData,
  closeButtonText,
  resultsText,
}: ListSearchPanelBlock<T>) => {
  const [query, setQuery] = useState('')
  const [queryInput, setQueryInput] = useState('')
  const [page, setPage] = useState(defaultFilter.page)
  const [results, setResults] = useState(Array<T>())
  const [data, setData] = useState({
    showNext: undefined,
    showPrev: undefined,
    hitsText: undefined,
  })
  const pageSize = defaultFilter.pageSize
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [filter, setFilter] = useState<filterType>({})
  const [filterOptions, setFilterOptions] = useState<filterOptionsType | undefined>(
    filterProps?.filterOptions,
  )
  const [isOpen, setIsOpen] = useState(false)

  const search = useCallback(async () => {
    let params = getParams()
    try {
      setLoading(true)
      updateCurrentHistoryEntry(params)
      await getAndSetSearchResults(params)
      if (!isOpen && queryInput !== '') {
        setIsOpen(true)
      }
    } catch (error) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [filter, page, pageSize, queryInput, queryUrl]) // for searchpage

  const getAndSetSearchResults = async (params: filterType) => {
    await axios
      .get(queryUrl, {
        params: params,
      })
      .then((res) => {
        if (res.data.value.filterOptions) setFilterOptions(res.data.value.filterOptions)
        setData(res.data.value)
        setResults(res.data.value.result)
        setError(false)
      })
      .catch((error) => {
        setError(true)
      })
  }

  // Retrieves the parameter and filter data from filter object
  const getParams = () => {
    let params: filterType = filter
    if (searchBarProps && queryInput !== '') {
      params.query = queryInput
    } else {
      params.query = ''
    }

    params.page = '' + page
    params.pageSize = '' + pageSize

    return params
  }

  const saveWindowHistoryState = () => {
    let params = getParams()
    const current: URL = new URL(window.location.toString())
    current.search = new URLSearchParams(params).toString()
    window.history.pushState(params, '', current) // Add the current page to the browser history before going to another page
  }

  const updateCurrentHistoryEntry = (params: filterType) => {
    const current: URL = new URL(window.location.toString())
    current.search = new URLSearchParams(params).toString()
    window.history.replaceState(params, '', current) // Updates the history browser state
  }

  // Check if any parameters is set in the url when rendering the page.
  useEffect(() => {
    const params: any = new Proxy(new URLSearchParams(window.location.search), {
      get: (searchParams, prop: any) => searchParams.get(prop),
    })

    setQueryInput(params.query ?? '')
    setPage(params.page ?? 1)

    if (filterProps) {
      let newFilter: filterType = {}

      Object.entries(filterProps.filterOptions).forEach(([key]) => {
        if (params.key) {
          newFilter[key] = params.key!
        }
      })

      setFilter(newFilter)
    }
    if (!searchBarProps && !filterProps) {
      search()
    }
  }, [])

  const notInitialRender = useRef(false)

  useEffect(() => {
    if (notInitialRender.current) {
      search()
    } else {
      notInitialRender.current = true
    }
  }, [search])

  useEffect(() => {
    const handlePopState = async () => {
      const currentUrl = new URL(window.location.href)
      const initialSelectedPage = currentUrl.searchParams.get('page') ?? '1'
      const currentSearch = currentUrl.searchParams.get('query') ?? ''

      setQuery(currentSearch)
      setQueryInput(currentSearch)
      setPage(parseInt(initialSelectedPage)) // Sets the page to the new page after back or forth button in browser have been pressed. This is to trigger a new search for new data on this new page.
    }

    window.addEventListener('popstate', handlePopState, false)

    return () => {
      window.removeEventListener('popstate', handlePopState, false)
    }
  }, [])

  const showResults = results.length > 0 && queryInput !== ''

  return (
    <div className='list-search-panel-block'>
      {searchBarProps && (
        <div className='list-search-panel-block__filter'>
          <Search
            searchButtonText={searchBarProps.searchButtonLabel}
            search={() => {
              saveWindowHistoryState()
              setQueryInput(query)
              setPage(1)
              setIsOpen(query !== '')
            }}
            onChange={(e) => {
              setQuery(e.target.value)
            }}
            resetSearchString={() => {
              saveWindowHistoryState()
              setQuery('')
              setPage(1)
            }}
            value={query}
            {...{
              text: searchBarProps.searchButtonLabel,
              placeholder: searchBarProps.placeHolderLabel,
              searchPageUrl: 'sokside',
              enpointUrl: 'sokside/search',
            }}
          />
        </div>
      )}
      <AnimatePresence>
        {isOpen &&
          (loading ? (
            <div className='list-search-panel-block__loading'>
              <Logo loop />
            </div>
          ) : error ? (
            <p>{errorMessage}</p>
          ) : (
            <motion.div
              className='list-search-panel-block__results'
              initial={{ height: 0, opacity: 0 }}
              animate={{
                height: 'auto',
                opacity: 1,
                transition: { duration: 0.8, ease: 'easeIn' },
              }}
              exit={{ height: 0, opacity: 0, transition: { duration: 0.8, ease: 'easeOut' } }}
              layout
            >
              <div className='list-search-panel-block__results__header'>
                <div className='list-search-panel-block__results__header__close'>
                  <span
                    className='list-search-panel-block__results__header__close-text'
                    id='close-text'
                  >
                    {closeButtonText}
                  </span>
                  <button onClick={() => setIsOpen(false)} aria-labelledby='close-text'>
                    <ClossRectangleIcon className='list-search-panel-block__results__header__icon' />
                  </button>
                </div>
                {filterProps && showResults && filterOptions && (
                  <div className={searchBarProps ? 'list-search-block__filter-box' : ''}>
                    <Filter
                      filterOptions={filterOptions}
                      setFilter={setFilter}
                      setPage={setPage}
                      filter={filter}
                      filterLabel={filterProps.filterLabel}
                      clearFilterText={filterProps.clearFilterText}
                    />
                  </div>
                )}
                <h3 className='list-search-panel-block__results__header__title'>
                  {showResults ? resultsText : noResultsText}
                </h3>
                {showResults && <p>{data.hitsText}</p>}
              </div>
              {showResults && (
                <>
                  {displayData(results)}
                  <PageNav
                    nextPage={() => {
                      saveWindowHistoryState()
                      setPage(page + 1)
                    }}
                    showNext={data.showNext}
                    showPrev={data.showPrev}
                    prevPage={() => {
                      saveWindowHistoryState()
                      setPage(page - 1)
                    }}
                  />
                </>
              )}
            </motion.div>
          ))}
      </AnimatePresence>
    </div>
  )
}
